import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import "./index.css";
import "./design/tokens.css"; // Phase-2 design-system tokens (OKLCH ramps + 4 themes as CSS vars)
import App from "./App";
import { initErrorReporter, initWebVitals } from "./lib/analytics";

// Observability (PERF-8 + TECH-5): field Core Web Vitals RUM (lazy web-vitals attribution chunk)
// and the ErrorBoundary `window.__onAppError` reporter. Both are consent-gated inside analytics.js
// (no event leaves the page before the optional-analytics opt-in, which only the public marketing
// surface offers) — registering here just makes the observers early enough to catch the metrics.
initErrorReporter();
initWebVitals();

// i18n robustness: the Google Website Translate widget rewrites the DOM (wrapping text in <font> nodes),
// which makes React's removeChild/insertBefore throw "NotFoundError" and crash the app — most visibly the
// LOGIN flow when it swaps the password step for the 2FA step in a non-English language. These guards
// no-op only the specific "node was already moved by Translate" case, so translation + React coexist.
if (typeof Node === "function" && Node.prototype) {
  const _removeChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child && child.parentNode !== this) return child;          // already detached by Translate
    return _removeChild.apply(this, arguments);
  };
  const _insertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) return newNode;   // ref moved by Translate
    return _insertBefore.apply(this, arguments);
  };
}

// SEO prerender (scripts/prerender.mjs) writes real HTML into #root for the public marketing
// routes — hydrate that instead of blowing it away. Non-prerendered pages (the /app SPA shell,
// portals, …) keep an empty #root and take the classic client render.
const rootEl = document.getElementById("root");

// HYD-1 — the hydration outcome is a MEASURED contract, not a console line.
//
// `onRecoverableError` used to only `console.warn` the React error. That downgrade is right for
// the user (a recovered mismatch is not a crash) but it left the failure INVISIBLE to everything
// that could act on it: nothing counts it, nothing gates on it, and the prerender crawl's own
// console collector only records `msg.type() === "error"`, so a warn is filtered out there too.
// A prerendered document whose root switches to client rendering throws away every byte of the
// snapshot that the entry-chunk budget, both Lighthouse budgets, the image-weight gate and the
// visual-regression gate exist to protect — and it does so silently, on 100% of loads.
//
// So publish the outcome on a stable global. `scripts/check-hydration.mjs` (run by
// `scripts/verify-prerender.mjs`) reads it per route and ratchets it against
// `scripts/hydration-budget.json`; a one-shot DOM event lets any in-page observer subscribe
// without scraping the console. Keep the console.warn — it is the human-readable half — and keep
// it a WARN: routing this through `window.__onAppError` would emit a `fatal: true` GA4 exception
// and a console.error on every marketing page, which is neither true nor safe for the e2e
// no-runtime-error sweep.
const hydration = { recovered: 0, errors: [] };
if (typeof window !== "undefined") window.__VP360_HYDRATION__ = hydration;

if (rootEl.hasChildNodes()) {
  hydrateRoot(rootEl, <App />, {
    // A recoverable hydration mismatch (e.g. a CMS-fed section whose first client render differs
    // from the snapshot) falls back to client rendering for that boundary — surface it as a
    // warning, not a crash-level console error.
    onRecoverableError: (err, errorInfo) => {
      const message = err && err.message ? err.message : String(err);
      hydration.recovered += 1;
      // Bounded: React reports the same mismatch once per retried hydration level, so an
      // unbounded array would grow with every future React retry policy change.
      if (hydration.errors.length < 20) {
        const componentStack = errorInfo && errorInfo.componentStack
          ? String(errorInfo.componentStack).slice(0, 2000)
          : null;
        hydration.errors.push({ message, componentStack });
      }
      console.warn("[hydration] recovered:", message);
      // One event per document — the signal is "this page did not hydrate", not "N errors".
      if (hydration.recovered === 1 && typeof window !== "undefined" && window.dispatchEvent) {
        try {
          window.dispatchEvent(new CustomEvent("vp:hydration-recovered", {
            detail: { path: window.location.pathname, message },
          }));
        } catch { /* observability must never break the page */ }
      }
    },
  });
} else {
  createRoot(rootEl).render(<App />);
}

if ("serviceWorker" in navigator) {
  // PERF-13: the app ships NO caching service worker any more — /sw.js stays deployed only as a
  // self-destruct stub for legacy clients still holding the old caching SW (their browser's
  // update-on-navigate check fetches it, it wipes caches and unregisters itself). Production
  // used to RE-register it on every load: a perpetual install→activate→unregister loop plus a
  // wasted /sw.js request per visit. New visits register nothing; instead every environment
  // actively unregisters whatever a previous session left behind and drops its caches, so a
  // reload always loads live code. (InstallPWA relies on `beforeinstallprompt` only — no SW.)
  navigator.serviceWorker.getRegistrations()
    .then((rs) => rs.forEach((r) => r.unregister())).catch(() => {});
  if (window.caches) {
    caches.keys().then((ks) => ks.forEach((k) => caches.delete(k))).catch(() => {});
  }
}
